// InvestmentTable.js
import React, { useState, useEffect } from 'react';
import MUIDataTable from 'mui-datatables';
import './investmentTable.css';
import "../../common/css/protagonistMUIDatatable.css";
import "../../common/css/icons.css";
import { TableCell, IconButton, Snackbar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPen } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { DeleteInvestment, EditInvestment } from '../../common/api/InvestmentAPIHelper';

const InvestmentTable = ({ data, onEdit, onDelete }) => {

  const [investmentData, setInvestmentData] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

    const handleDelete = async (investmentId) => {
      const confirmed = window.confirm('Are you sure you want to delete the investment?');
      if (confirmed) {
        const response = await DeleteInvestment(investmentId);
        if (response && response.status === 200) {
          setSnackbarMessage(`Successfully deleted investment with ID ${investmentId}`);
          onDelete();
        } else {
          console.error('Error:', response.status, response.statusText);
          setSnackbarMessage(`Failed to delete investment with ID ${investmentId}`);
        }
        setSnackbarOpen(true);
      }
    };

    const handleEdit = (investmentId) => {
      const investment = data.find(investment => investment.investmentId === investmentId);  // Fetch the whole fund object
      onEdit(investment); // Pass the investment object to the onEdit callback
  };

    const customInvestmentIdRender = (value, tableMeta, updateValue) => {
      const investmentId = value;
      const handleDeleteClick = () => handleDelete(investmentId);
      const handleEditClick = () => handleEdit(investmentId, tableMeta.rowData);
  
      return (
        <div>
          <IconButton onClick={handleEditClick}>
            <FontAwesomeIcon icon={faPen} className="ra__protMUIDatatable_icon" /> {/* Edit icon */}
          </IconButton>
          <IconButton onClick={handleDeleteClick}>
            <FontAwesomeIcon icon={faTrashAlt} className="ra__protMUIDatatable_icon" /> {/* Delete icon */}
          </IconButton>
        </div>
      );
    };

    const customHeadRender = ({ label }) => {
        return (
          <TableCell style={{ textTransform: 'capitalize' }}>
            {label}
          </TableCell>
        );
      };

      function formatCurrency(value) {
        if (!value || isNaN(value)) {
          value = 0;
        }
      
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      }
      

      const formatPercentage = (value) => {
        return `${(value * 100).toFixed(2)}%`;
      };

      const customDateRender = (value, tableMeta, updateValue) => {
        const investmentDate = new Date(value);
        const localDate = new Date(investmentDate.getTime() - investmentDate.getTimezoneOffset() * 60000);
        return format(localDate, 'do MMM, yyyy');
      };
      

      const columns = [
        {
          name: 'investmentId',
          label: 'Edit / Delete',
          options: {
            customHeadRender: customHeadRender,
            customBodyRender: customInvestmentIdRender,
          },
        },
        {
          name: 'fundName',
          label: 'Fund Name',
          options: {
            customHeadRender: customHeadRender,
            display: 'excluded',
          },
        },
        {
          name: 'currentAum',
          label: 'Current Assets Under Management (AUM)',
          options: {
            customHeadRender: customHeadRender,
            customBodyRender: formatCurrency,
            display: 'excluded',
          },
        },
        {
          name: 'portcoName',
          label: 'Portfolio Company Name',
          options: {
            customHeadRender: customHeadRender,
          },
        },
        {
          name: 'portcoLegalName',
          label: 'Portfolio Company Legal Name',
          options: {
            customHeadRender: customHeadRender,
          },
        },
        
        {
          name: 'investmentAmount',
          label: 'Investment Amount',
          options: {
            customHeadRender: customHeadRender,
            customBodyRender: formatCurrency,
          },
        },
        {
          name: 'investmentDate',
          label: 'Investment Date',
          options: {
            customHeadRender: customHeadRender,
             customBodyRender: customDateRender,
          },
        },
        {
          name: 'investmentRound',
          label: 'Investment Round',
          options: {
            customHeadRender: customHeadRender,
          },
        },
        {
          name: 'investmentType',
          label: 'Investment Type',
          options: {
            customHeadRender: customHeadRender,
          },
        },
        {
          name: 'percentOfFundAum',
          label: 'Percent of Current Fund AUM',
          options: {
            customHeadRender: customHeadRender,
            customBodyRender: formatPercentage,
          },
        },
      ];

      const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage('');
      };

  const options = {
    filter: true,
    filterType: 'dropdown',
    responsive: 'standard',
    serverSide: false,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 15],
    selectableRows: 'none',
    expandableRows: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    customToolbarSelect: () => {},
    setTableProps: () => {
      return {
        padding: 'none',
        size: 'medium',
      };
    },
    textLabels: {
      body: {
        noMatch: 'Sorry, no matching records found',
      },
    },
    sortOrder: {
      name: 'investmentDate',
      direction: 'asc'
    }
  };

  return (
    <div>
      <MUIDataTable
        title={'Investments'}
        data={data}
        columns={columns}
        options={options}
        className="protMUIDatatable"
        style={{ width: '80%' }}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        className={snackbarMessage.includes('Successfully') ? '.ra__protMUIDatatable_snackbar' : '.ra__protMUIDatatable_snackbar error'}
      />
    </div>
  );
};

export default InvestmentTable;
